import i18n from "i18next";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { floatColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { useAavoToast } from "src/components/common/toast/AavoToastContext.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import {
	formatPartCostOverrideConditions,
	getPartCostOverrideOwnerApi,
	PartCostOverrideOwner,
	PartCostOverrideRow,
} from "src/components/views/erp/sales/partCostOverride/partCostOverrideOwnerApi.ts";
import { PartCostOverrideForm } from "src/components/views/erp/sales/partCostOverride/PartCostOverrideForm.tsx";

export interface PartCostOverridesDataGridProps {
	owner: PartCostOverrideOwner;
	editable: boolean;
	onCostsSaved: () => Promise<unknown>;
}

export function PartCostOverridesDataGrid({ owner, editable, onCostsSaved }: PartCostOverridesDataGridProps) {
	const { showToast } = useAavoToast();
	const api = getPartCostOverrideOwnerApi(owner);

	return (
		<ClientSideDataGridModel
			gridId={"6C1F2B7E0D9A4E35"}
			fetchData={api.getPartCostOverrides}
			getRows={(data) => data}
			initialParams={{}}
			getRowId={(row) => row.partCostOverrideId}
			render={({ dataGridProps, refreshData }) => {
				async function afterCostsChanged() {
					showToast({
						title: i18n.t("part_cost_overrides_saved"),
						message: i18n.t("part_cost_overrides_saved_recalculation_required"),
					});
					await Promise.all([refreshData(), onCostsSaved()]);
				}

				return (
					<FormCrudDataGrid<PartCostOverrideRow>
						columns={[
							textColumn({
								field: "partNo",
								headerName: i18n.t("part_no"),
								width: 150,
							}),
							textColumn({
								field: "partDescription_1",
								headerName: i18n.t("description"),
								width: 250,
							}),
							textColumn({
								field: "propertyConditions",
								headerName: i18n.t("conditions"),
								valueGetter: (_, row) => formatPartCostOverrideConditions(row.propertyConditions),
								width: 350,
							}),
							floatColumn({
								field: "unitCost",
								headerName: i18n.t("unit_cost"),
								maxDigits: 4,
								width: 130,
							}),
						]}
						form={{
							addRowEnabled: editable,
							editEnabled: editable,
							dialogTitle: i18n.t("part_cost_override"),
							dialogSize: "md",
							agentContext: (row) => ({
								key: defineAgentUiContextKey("erp.sales.partCostOverrides.formDialog"),
								pathSegments: [i18n.t("part_cost_override")],
								data: { owner, partCostOverrideId: row?.partCostOverrideId ?? null },
							}),
							component: ({ row, onCompleted, onFormEdited }) => (
								<PartCostOverrideForm
									api={api}
									existingPartCostOverride={row}
									onFormEdited={onFormEdited}
									onCompleted={async (result) => {
										if (result.type === "success") await afterCostsChanged();
										onCompleted(
											result.type === "success" ?
												{ type: "success", value: row?.partCostOverrideId }
											:	result,
										);
									}}
								/>
							),
						}}
						remove={
							editable ?
								async ({ items }) => {
									for (const item of items) {
										await api.deletePartCostOverride(item.partCostOverrideId);
									}
									await afterCostsChanged();
								}
							:	undefined
						}
						agentContext={{
							key: defineAgentUiContextKey("erp.sales.partCostOverrides.grid"),
							data: { owner },
							rowFields: ["partCostOverrideId", "partId", "partNo", "propertyConditions", "unitCost"],
							selectedRowPathSegment: (row) => `${i18n.t("part_cost_override")} ${row.partNo}`,
						}}
						{...dataGridProps}
					/>
				);
			}}
		/>
	);
}
